import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  Tooltip,
  Checkbox,
  Stack,
  Typography,
} from "@mui/material";
import {
  formatAmount,
  formatHistoryAmount,
  isAutoload,
  formatPdfDate,
} from "../../../utils/hooks/dataHandler";
import { AdjustmentChip, headerStyle, EmptyState } from "./DetailsTable";
import { getSignedUnutilisedLossAmount } from "../Pdf/shared";
import CustomDatePicker from "../../utils/DatePicker/CustomDatePicker";
import {
  CalculatorTextField,
  TextFieldForAmount,
} from "../../utils/TextField/NumericTextField";
import { StyledIconButton } from "../../utils/Icon";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  TextFieldForName,
  TextFieldForViewOnly,
} from "../../utils/TextField/TextField";
import TaxField from "../../utils/TextField/TaxField";

const FINANCIAL_YEAR_DATA_IDS = [
  "unutilised_losses",
  "unutilised_donations",
  "current_year_donations",
];

const TABLE_SX = {
  backgroundColor: "background.paper",
  overflow: "hidden",
  borderColor: "divider",
  borderWidth: 1,
  borderStyle: "solid",
};

const SCROLL_BOX_SX = (theme, maxHeight) => ({
  maxHeight,
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: `${alpha(theme.palette.primary.main, 0.2)} transparent`,
  "&::-webkit-scrollbar": {
    width: "6px",
    height: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: alpha(theme.palette.primary.main, 0.2),
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: alpha(theme.palette.primary.main, 0.4),
  },
});

const TABLE_ELEMENT_SX = {
  borderCollapse: "separate",
  borderSpacing: 0,
};

const getHeaderColumns = (dataId, isCrossYA) => [
  {
    id: "period",
    label: getPeriodLabel(dataId),
    align: "left",
    width: isCrossYA ? "22%" : "25%",
  },
  ...(isCrossYA
    ? [
        {
          id: "preceding_amount",
          label: "Preceding Amount",
          align: "right",
          width: "21%",
        },
        {
          id: "current_amount",
          label: "Current Amount",
          align: "right",
          width: "21%",
        },
      ]
    : [
        {
          id: "amount",
          label: "Amount",
          align: "right",
          width: "45%",
        },
      ]),
  {
    id: "reference",
    label: "Reference",
    align: "center",
    width: isCrossYA ? "36%" : "30%",
  },
];

const CELL_SX = {
  borderColor: "divider",
  py: 1.25,
};

const INLINE_RIGHT_BOX_SX = {
  width: "100%",
  textAlign: "right",
};

const ACTION_BOX_SX = {
  width: "100%",
};

const ROW_ACTIONS_SX = (theme, rowState, isHovered = false) => {
  const tone = getRowTone(theme, rowState);

  return {
    bgcolor: isHovered ? tone.hoverBg : tone.panelBg,
    borderTop: 0,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
    borderLeft: `3px solid ${isHovered ? tone.indicator : "transparent"}`,
    borderRight: 0,
    boxShadow: tone.shadow,
    py: 1,
    px: 2,
  };
};

const ACTION_GROUP_SX = {
  rowGap: 0.75,
  columnGap: 1.5,
  flexWrap: "wrap",
  minHeight: 40,
};

const DECLARATION_SECTION_SX = (theme, isAgreementDefined) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "nowrap",
  gap: isAgreementDefined ? 1.5 : 0,
  flex: isAgreementDefined ? "1 1 auto" : "0 1 300px",
  maxWidth: isAgreementDefined ? "none" : 360,
  px: 1.25,
  py: 0.75,
  borderRadius: 1,
  backgroundColor: isAgreementDefined
    ? alpha(theme.palette.primary.main, 0.025)
    : theme.palette.background.default,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
  transition: "background-color 0.2s ease, border-color 0.2s ease",
});

const DECLARATION_PROMPT_SX = (isAgreementDefined) => ({
  width: isAgreementDefined ? "auto" : "100%",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: isAgreementDefined ? "flex-start" : "space-between",
  gap: 1,
  minHeight: 28,
});

const DECLARATION_TEXT_SX = {
  fontWeight: 600,
  fontSize: 12,
  color: "text.secondary",
  letterSpacing: 0,
  whiteSpace: "nowrap",
};

const FORFEITED_NOTICE_SX = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  flex: "1 1 auto",
  px: 1.25,
  py: 0.75,
  borderRadius: 1,
  backgroundColor: alpha(theme.palette.error.main, 0.035),
  border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
  maxWidth: "485px",
});

const FORFEITED_NOTICE_TEXT_SX = {
  fontWeight: 600,
  fontSize: 12,
  color: "error.main",
  letterSpacing: 0,
};

const CONTROL_AREA_SX = {
  display: "flex",
  justifyContent: "flex-end",
  flexShrink: 0,
};

const DECLARATION_DETAILS_SX = {
  gap: 1.5,
  flexWrap: "nowrap",
  alignItems: "center",
  whiteSpace: "nowrap",
};

const INLINE_CONTROL_SX = {
  gap: 0.5,
  minWidth: 96,
};

const INLINE_LABEL_SX = {
  fontSize: 11,
  fontWeight: 600,
  color: "text.secondary",
  letterSpacing: 0,
};

const NOA_CONTROL_SX = {
  gap: 0.75,
  minWidth: 200,
};

const ACTION_BUTTONS_SX = {
  ml: "auto",
};

const SPACER_CELL_SX = {
  border: 0,
  height: 8,
  p: 0,
};

const DECLARATION_TOOLTIP =
  "Enable this when the agreement status should be captured and printed.";
const AGREEMENT_TOOLTIP =
  "Unticked means explicitly not agreed once declaration is enabled.";
const NOA_TOOLTIP = "Optional NOA date for this declared agreement status.";

const isFinancialYearData = (dataId) =>
  FINANCIAL_YEAR_DATA_IDS.includes(dataId);

const getPeriodLabel = (dataId) =>
  isFinancialYearData(dataId) ? "Financial year ended" : "Year of Assessment";

const getMainColumnCount = (currency, isCrossYA) => {
  const baseColumns = isCrossYA ? 4 : 3;
  return currency !== "SGD" ? baseColumns + 1 : baseColumns;
};

const getPeriodField = (dataId) =>
  isFinancialYearData(dataId) ? "date" : "yearOfAssessment";

const getSelectedPeriodDate = (row, dataId) => {
  if (isFinancialYearData(dataId)) {
    return row.date ? new Date(row.date) : null;
  }

  return row.yearOfAssessment ? new Date(row.yearOfAssessment, 0) : null;
};

const getReadOnlyPeriodValue = (row, dataId) => {
  const sourceLabel =
    String(row?.name || "").trim() || String(row?.schedule || "").trim();
  if (sourceLabel) return sourceLabel;

  if (isFinancialYearData(dataId)) {
    const rawDate = row?.date;
    if (
      typeof rawDate === "number" &&
      Number.isInteger(rawDate) &&
      rawDate >= 1900 &&
      rawDate <= 9999
    ) {
      return String(rawDate);
    }

    const parsedDate = rawDate ? new Date(rawDate) : null;
    return parsedDate &&
      !Number.isNaN(parsedDate.getTime()) &&
      parsedDate.getFullYear() !== 1970
      ? String(parsedDate.getFullYear())
      : "";
  }

  return String(row?.yearOfAssessment || "");
};

const getPeriodValue = (date, dataId) => {
  if (!date) return "";
  return isFinancialYearData(dataId) ? date : date.getFullYear();
};

const getMaxPeriodDate = (dataId, yearOfAssessment) =>
  new Date(
    new Date().setFullYear(
      dataId === "unutilised_capital_allowances"
        ? yearOfAssessment - 1
        : yearOfAssessment - 2
    )
  );

const getRowKey = (row, index, dataId) => {
  if (row?.id) return row.id;
  if (row?.row_id) return row.row_id;
  if (row?._id) return row._id;

  const period = row?.date || row?.yearOfAssessment || "new";
  const source = row?.reference || row?.schedule || row?.status || "";
  const amount = row?.amount ?? "";

  if (period !== "new" || source) {
    return `${dataId}-${period}-${source}-${amount}`;
  }

  return `${dataId}-new-${index}`;
};

const getRowState = (row, canEdit) => {
  const isForfeited = Boolean(row?.isForfeited);
  const isAutoloaded = isAutoload(row?.status);
  const isSystemRow = Boolean(row?.systemData);

  return {
    isForfeited,
    isAutoloaded,
    isSystemRow,
    isRowDisabled: !canEdit || isSystemRow || isAutoloaded || isForfeited,
  };
};

const getRowTone = (theme, { isForfeited, isRowDisabled }, rowIndex = 0) => {
  const zebraBg =
    rowIndex % 2 === 0
      ? theme.palette.background.paper
      : alpha(theme.palette.grey[500], 0.035);

  if (isForfeited) {
    return {
      bg: alpha(theme.palette.error.main, 0.06),
      panelBg: alpha(theme.palette.error.main, 0.06),
      hoverBg: alpha(theme.palette.error.main, 0.09),
      border: alpha(theme.palette.error.main, 0.28),
      divider: alpha(theme.palette.error.main, 0.18),
      indicator: alpha(theme.palette.error.main, 0.72),
      shadow: "none",
    };
  }

  if (isRowDisabled) {
    return {
      bg: alpha(theme.palette.grey[500], 0.045),
      panelBg: alpha(theme.palette.grey[500], 0.07),
      hoverBg: alpha(theme.palette.grey[500], 0.09),
      border: alpha(theme.palette.grey[500], 0.2),
      divider: alpha(theme.palette.grey[500], 0.16),
      indicator: alpha(theme.palette.grey[500], 0.45),
      shadow: "none",
    };
  }

  return {
    bg: zebraBg,
    panelBg: alpha(theme.palette.grey[500], 0.025),
    hoverBg: alpha(theme.palette.primary.main, 0.09),
    border: alpha(theme.palette.divider, 0.14),
    divider: alpha(theme.palette.primary.main, 0.12),
    indicator: theme.palette.primary.main,
    shadow: "none",
  };
};

const getMainRowSx = (theme, rowState, rowIndex, isHovered = false) => {
  const tone = getRowTone(theme, rowState, rowIndex);

  return {
    "& > .MuiTableCell-root": {
      bgcolor: isHovered ? tone.hoverBg : tone.bg,
      borderTop: 0,
      borderBottom: `1px solid ${tone.border}`,
      py: 1.25,
      transition: "background-color 0.2s ease, border-color 0.2s ease",
    },
    "& > .MuiTableCell-root:first-of-type": {
      borderLeft: `3px solid ${isHovered ? tone.indicator : "transparent"}`,
      boxShadow: tone.shadow,
    },
    "& > .MuiTableCell-root:last-of-type": {
      borderRight: 0,
    },
  };
};

const getActionRowSx = (theme, rowState, rowIndex, isHovered = false) => {
  const tone = getRowTone(theme, rowState, rowIndex);

  return {
    "& > .MuiTableCell-root": {
      bgcolor: isHovered ? tone.hoverBg : tone.panelBg,
      transition: "background-color 0.2s ease, border-color 0.2s ease",
    },
  };
};

const getHeaderCellSx = (theme, width) => ({
  ...headerStyle(theme),
  width,
});

const getHistoryTooltip = (row) => {
  if (isAutoload(row?.status)) return "";
  if (!row?.historyAmount || row.historyAmount.length === 0) return "";
  return formatHistoryAmount(row.historyAmount);
};

const formatAgreementValue = (row) => {
  if (!row.toDeclareAgreement) return "";
  return row.agreementStatus?.isAgreed ? "Yes" : "No";
};

const useStableCallback = (callback) => {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useCallback((...args) => callbackRef.current?.(...args), []);
};

const TableHeader = memo(({ dataId, currency, isCrossYA, theme }) => {
  const columns = useMemo(() => {
    const baseColumns = getHeaderColumns(dataId, isCrossYA);

    if (currency !== "SGD") {
      baseColumns.push({
        id: "exchangeRate",
        label: "Historical Exchange Rate",
        align: "center",
        width: "30%",
      });
    }

    return baseColumns;
  }, [currency, dataId, isCrossYA]);

  return (
    <TableHead>
      <TableRow>
        {columns.map((column) => (
          <TableCell
            key={column.id}
            align={column.align}
            sx={getHeaderCellSx(theme, column.width)}
          >
            {column.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
});

const PeriodCell = memo(
  ({
    canEdit,
    company,
    dataId,
    isAutoloaded,
    isReadOnly = false,
    isRowDisabled,
    row,
    onFieldChange,
  }) => {
    if (!canEdit || isReadOnly || isAutoloaded) {
      return (
        <TextFieldForViewOnly
          value={getReadOnlyPeriodValue(row, dataId)}
          sx={{ fontWeight: 500 }}
        />
      );
    }

    return (
      <CustomDatePicker
        selected={getSelectedPeriodDate(row, dataId)}
        onChange={(date) =>
          onFieldChange(getPeriodField(dataId), getPeriodValue(date, dataId))
        }
        maxDate={getMaxPeriodDate(dataId, company.yearOfAssessment)}
        showYearPicker
        dateFormat='yyyy'
        disabled={isRowDisabled}
      />
    );
  }
);

const AmountCell = memo(
  ({
    canEdit,
    field = "amount",
    historyField = "historyAmount",
    isAutoloaded,
    isAutoloadedLoss = false,
    isReadOnly = false,
    showAdjustment = false,
    isRowDisabled,
    row,
    onFieldChange,
    isSystemCurrentYearLoss = false,
  }) => {
    const historyValue = row?.[historyField];
    const calculatorValue =
      Array.isArray(historyValue) && historyValue.length > 0
        ? historyValue
        : [row?.[field] || 0];
    const adjustment = row.taxAdjustment || (row.systemData ? "Deduct" : "");
    const isTypographyAmount = isReadOnly || (isAutoloadedLoss && isAutoloaded);

    return (
      <TableCell align='center' sx={CELL_SX}>
        <Box
          display='flex'
          alignItems='center'
          justifyContent='flex-end'
          gap={1}
        >
          {(!isRowDisabled || isAutoloaded || isSystemCurrentYearLoss) && (
            <AdjustmentChip
              type={showAdjustment || adjustment ? adjustment : "Deduct"}
            />
          )}
          <Tooltip title={getHistoryTooltip(row)} placement='top'>
            <Box display='inline-block' sx={INLINE_RIGHT_BOX_SX}>
              {isTypographyAmount ? (
                <Typography variant='body2' fontWeight={500}>
                  {isSystemCurrentYearLoss
                    ? formatAmount(-(row?.[field] ?? 0))
                    : formatAmount(
                        isAutoloadedLoss
                          ? getSignedUnutilisedLossAmount(row, field)
                          : (row?.[field] ?? 0)
                      )}
                </Typography>
              ) : canEdit ? (
                isAutoloaded ? (
                  <TextFieldForAmount
                    value={Math.abs(row?.[field] || 0)}
                    disabled={true}
                    fullWidth
                  />
                ) : (
                  <CalculatorTextField
                    value={calculatorValue}
                    onChange={(newValue) => onFieldChange(field, newValue)}
                    fullWidth={true}
                    disabled={isRowDisabled}
                    sx={{
                      bgcolor: isRowDisabled
                        ? "background.default"
                        : "background.paper",
                    }}
                  />
                )
              ) : (
                <TextFieldForViewOnly
                  value={formatAmount(
                    isAutoloaded
                      ? Math.abs(row?.[field] ?? 0)
                      : (row?.[field] ?? 0)
                  )}
                />
              )}
            </Box>
          </Tooltip>
        </Box>
      </TableCell>
    );
  }
);

const ReferenceCell = memo(
  ({
    canEdit,
    isFixedSchedule,
    isReadOnly = false,
    isSystemRow,
    row,
    onFieldChange,
  }) => (
    <TableCell align='center'>
      {isFixedSchedule ? (
        <TextFieldForViewOnly
          value={row.schedule || "-"}
          sx={{ fontWeight: 500 }}
        />
      ) : isReadOnly ? (
        <TextFieldForViewOnly
          value={row.reference || row.name || "-"}
          sx={{ fontWeight: 500 }}
        />
      ) : canEdit ? (
        <TextFieldForName
          size='small'
          value={row.reference || ""}
          onChange={(newValue) => onFieldChange("reference", newValue)}
          fullWidth
          disabled={isSystemRow}
          placeholder='Add note...'
        />
      ) : (
        <TextFieldForViewOnly value={row.reference || ""} />
      )}
    </TableCell>
  )
);

const AgreementControl = memo(
  ({ canEdit, isRowDisabled, row, onFieldChange }) => {
    const isAgreementDefined = row.toDeclareAgreement === true;

    if (!isAgreementDefined) return null;

    return (
      <Tooltip title={AGREEMENT_TOOLTIP} placement='top'>
        <Stack direction='row' alignItems='center' sx={INLINE_CONTROL_SX}>
          <Typography component='span' sx={INLINE_LABEL_SX}>
            Agreed?
          </Typography>
          {canEdit ? (
            <Checkbox
              checked={
                isAgreementDefined && Boolean(row.agreementStatus?.isAgreed)
              }
              onChange={(event) =>
                onFieldChange("isAgreed", event.target.checked)
              }
              disabled={isRowDisabled || !isAgreementDefined}
              size='small'
            />
          ) : (
            <TextFieldForViewOnly value={formatAgreementValue(row)} />
          )}
        </Stack>
      </Tooltip>
    );
  }
);

const NoaDateControl = memo(
  ({ canEdit, isRowDisabled, row, onFieldChange }) => {
    const isAgreementDefined = row.toDeclareAgreement === true;
    const dateOfNOA = row.agreementStatus?.dateOfNOA;

    if (!isAgreementDefined) return null;

    return (
      <Tooltip title={NOA_TOOLTIP} placement='top'>
        <Stack direction='row' alignItems='center' sx={NOA_CONTROL_SX}>
          <Typography component='span' sx={INLINE_LABEL_SX}>
            Date of NOA
          </Typography>
          {canEdit ? (
            <CustomDatePicker
              selected={dateOfNOA ? new Date(dateOfNOA) : null}
              onChange={(date) => onFieldChange("dateOfNOA", date)}
              disabled={isRowDisabled || !isAgreementDefined}
            />
          ) : (
            <TextFieldForViewOnly
              value={isAgreementDefined ? formatPdfDate(dateOfNOA) : ""}
            />
          )}
        </Stack>
      </Tooltip>
    );
  }
);

const DeclarationSection = memo(({ canEdit, row, theme, onFieldChange }) => {
  const isAgreementDefined = row.toDeclareAgreement === true;

  return (
    <Box sx={DECLARATION_SECTION_SX(theme, isAgreementDefined)}>
      <Stack direction='row' sx={DECLARATION_PROMPT_SX(isAgreementDefined)}>
        <Tooltip title={DECLARATION_TOOLTIP} placement='top' arrow>
          <Box sx={{ cursor: "default" }}>
            <Typography component='span' sx={DECLARATION_TEXT_SX}>
              Declare Agreement Status
            </Typography>
          </Box>
        </Tooltip>

        <Box sx={CONTROL_AREA_SX}>
          {canEdit ? (
            <Checkbox
              checked={isAgreementDefined}
              onChange={(event) =>
                onFieldChange("toDeclareAgreement", event.target.checked)
              }
              size='small'
            />
          ) : (
            <TextFieldForViewOnly
              value={isAgreementDefined ? "Yes" : ""}
              size='small'
            />
          )}
        </Box>
      </Stack>

      {isAgreementDefined && (
        <Stack direction='row' sx={DECLARATION_DETAILS_SX}>
          <AgreementControl
            canEdit={canEdit}
            row={row}
            onFieldChange={onFieldChange}
          />
          <NoaDateControl
            canEdit={canEdit}
            row={row}
            onFieldChange={onFieldChange}
          />
        </Stack>
      )}
    </Box>
  );
});

const ForfeitedNotice = memo(({ theme }) => (
  <Box sx={FORFEITED_NOTICE_SX(theme)}>
    <BlockIcon fontSize='small' color='error' />
    <Typography component='span' sx={FORFEITED_NOTICE_TEXT_SX}>
      This row has been forfeited and is disregarded from the computation.
    </Typography>
  </Box>
));

const ExchangeRateCell = memo(
  ({ canEdit, currencyRate, isRowDisabled, row, onFieldChange }) => (
    <TableCell align='center'>
      {canEdit ? (
        <TaxField
          size='small'
          value={row.exchangeRate || currencyRate}
          onChange={(newValue) => onFieldChange("exchangeRate", newValue)}
          decimalScale={4}
          fullWidth
          disabled={isRowDisabled}
        />
      ) : (
        <TextFieldForViewOnly value={row.exchangeRate || ""} />
      )}
    </TableCell>
  )
);

const RowActionButtons = memo(
  ({
    index,
    isForfeited,
    isSystemRow,
    isAutoloaded,
    row,
    onForfeit,
    onRemove,
  }) => (
    <Tooltip
      title={
        (row.status === "autoload" ||
          row.reference === "[Auto Generated] Current Year Losses") &&
        "This row is from a source or auto generated."
      }
      placement='top'
    >
      <Box display='inline-block' sx={ACTION_BOX_SX}>
        <StyledIconButton
          onClick={() => onForfeit(index)}
          icon={isForfeited ? ReplayIcon : BlockIcon}
          tooltip={isForfeited ? "Restore Row" : "Forfeit Row"}
          iconColor={isForfeited ? "warning.main" : "info.main"}
          disabled={isAutoloaded || isSystemRow}
        />
        {!isForfeited && (
          <StyledIconButton
            onClick={() => onRemove(index)}
            icon={DeleteIcon}
            tooltip='Remove Row'
            iconColor='error.main'
            disabled={isAutoloaded || isSystemRow || isForfeited}
          />
        )}
      </Box>
    </Tooltip>
  )
);

const UnutilisedRow = memo(
  ({
    canEdit,
    company,
    currency,
    currencyRate,
    dataId,
    index,
    isCrossYA,
    row,
    theme,
    onForfeit,
    onRemove,
    onUpdate,
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const rowState = useMemo(() => getRowState(row, canEdit), [canEdit, row]);
    const mainRowSx = useMemo(
      () => getMainRowSx(theme, rowState, index, isHovered),
      [index, isHovered, rowState, theme]
    );
    const actionRowSx = useMemo(
      () => getActionRowSx(theme, rowState, index, isHovered),
      [index, isHovered, rowState, theme]
    );
    const actionCellSx = useMemo(
      () => ROW_ACTIONS_SX(theme, rowState, isHovered),
      [isHovered, rowState, theme]
    );
    const isAutoloadedLoss =
      dataId === "unutilised_losses" && rowState.isAutoloaded;
    const isSystemCurrentYearLoss =
      dataId === "unutilised_losses" && rowState.isSystemRow;
    const isReadOnlyLoss = isAutoloadedLoss || isSystemCurrentYearLoss;
    const handleGroupMouseEnter = useCallback(() => setIsHovered(true), []);
    const handleGroupMouseLeave = useCallback(() => setIsHovered(false), []);
    const handleFieldChange = useCallback(
      (field, value) => onUpdate({ field, index, value }),
      [index, onUpdate]
    );

    return (
      <>
        <TableRow
          sx={mainRowSx}
          onMouseEnter={handleGroupMouseEnter}
          onMouseLeave={handleGroupMouseLeave}
        >
          <TableCell>
            <PeriodCell
              canEdit={canEdit}
              company={company}
              dataId={dataId}
              isAutoloaded={isAutoloadedLoss}
              isReadOnly={isReadOnlyLoss}
              isRowDisabled={rowState.isRowDisabled}
              row={row}
              onFieldChange={handleFieldChange}
            />
          </TableCell>
          {isCrossYA ? (
            <>
              <AmountCell
                canEdit={canEdit}
                field='preceding_amount'
                historyField='precedingHistoryAmount'
                isAutoloaded={rowState.isAutoloaded}
                isAutoloadedLoss={isAutoloadedLoss}
                isReadOnly={isReadOnlyLoss}
                showAdjustment={isAutoloadedLoss}
                isRowDisabled={rowState.isRowDisabled}
                row={row}
                onFieldChange={handleFieldChange}
                isSystemCurrentYearLoss={isSystemCurrentYearLoss}
              />
              <AmountCell
                canEdit={canEdit}
                field='current_amount'
                historyField='currentHistoryAmount'
                isAutoloaded={rowState.isAutoloaded}
                isAutoloadedLoss={isAutoloadedLoss}
                isReadOnly={isReadOnlyLoss}
                isRowDisabled={rowState.isRowDisabled}
                row={row}
                onFieldChange={handleFieldChange}
                isSystemCurrentYearLoss={isSystemCurrentYearLoss}
              />
            </>
          ) : (
            <AmountCell
              canEdit={canEdit}
              isAutoloaded={rowState.isAutoloaded}
              isAutoloadedLoss={isAutoloadedLoss}
              isReadOnly={isReadOnlyLoss}
              showAdjustment={isAutoloadedLoss}
              isRowDisabled={rowState.isRowDisabled}
              row={row}
              onFieldChange={handleFieldChange}
              isSystemCurrentYearLoss={isSystemCurrentYearLoss}
            />
          )}
          <ReferenceCell
            canEdit={canEdit}
            isFixedSchedule={isAutoloadedLoss}
            isReadOnly={isReadOnlyLoss}
            isSystemRow={rowState.isSystemRow}
            row={row}
            onFieldChange={handleFieldChange}
          />
          {currency !== "SGD" && (
            <ExchangeRateCell
              canEdit={canEdit}
              currencyRate={currencyRate}
              isRowDisabled={rowState.isRowDisabled}
              row={row}
              onFieldChange={handleFieldChange}
            />
          )}
        </TableRow>
        {!isSystemCurrentYearLoss && !isAutoloadedLoss && (
          <>
            <TableRow
              sx={actionRowSx}
              onMouseEnter={handleGroupMouseEnter}
              onMouseLeave={handleGroupMouseLeave}
            >
              <TableCell
                colSpan={getMainColumnCount(currency, isCrossYA)}
                sx={actionCellSx}
              >
                <Stack direction='row' alignItems='center' sx={ACTION_GROUP_SX}>
                  {row.isForfeited ? (
                    <ForfeitedNotice theme={theme} />
                  ) : (
                    <DeclarationSection
                      canEdit={canEdit}
                      row={row}
                      theme={theme}
                      onFieldChange={handleFieldChange}
                    />
                  )}
                  {canEdit && (
                    <Stack
                      direction='row'
                      alignItems='center'
                      sx={ACTION_BUTTONS_SX}
                    >
                      <RowActionButtons
                        index={index}
                        isForfeited={rowState.isForfeited}
                        isSystemRow={rowState.isSystemRow}
                        isAutoloaded={rowState.isAutoloaded}
                        row={row}
                        onForfeit={onForfeit}
                        onRemove={onRemove}
                      />
                    </Stack>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                colSpan={getMainColumnCount(currency, isCrossYA)}
                sx={SPACER_CELL_SX}
              />
            </TableRow>
          </>
        )}
      </>
    );
  }
);

const UnutilisedTable = ({
  canEdit = true,
  dataId,
  currency,
  currencyRate,
  company,
  data = [],
  isCrossYA = false,
  noRowsHint = "No adjustment details available.",
  maxHeight = 500,
  onUpdate,
  onRemove,
  onForfeit,
}) => {
  const theme = useTheme();
  const handleUpdate = useStableCallback(onUpdate);
  const handleRemove = useStableCallback(onRemove);
  const handleForfeit = useStableCallback(onForfeit);

  if (!data || data.length === 0) {
    return (
      <EmptyState
        message='No adjustments found for this period.'
        description={noRowsHint}
      />
    );
  }

  return (
    <Box sx={TABLE_SX}>
      <Box sx={SCROLL_BOX_SX(theme, maxHeight)}>
        <Table stickyHeader size='small' sx={TABLE_ELEMENT_SX}>
          <TableHeader
            currency={currency}
            dataId={dataId}
            isCrossYA={isCrossYA}
            theme={theme}
          />
          <TableBody>
            {data.map((row, index) => (
              <UnutilisedRow
                key={getRowKey(row, index, dataId)}
                canEdit={canEdit}
                company={company}
                currency={currency}
                currencyRate={currencyRate}
                dataId={dataId}
                index={index}
                isCrossYA={isCrossYA}
                row={row}
                theme={theme}
                onForfeit={handleForfeit}
                onRemove={handleRemove}
                onUpdate={handleUpdate}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default UnutilisedTable;
